/* eslint-disable jsx-a11y/no-noninteractive-tabindex */
import React, { useCallback, useEffect } from 'react';

import styled, { css } from 'styled-components';

import { getProductCtaLabel } from '~/pdp/utils/getProductCtaLabel';
import { CountryCodes } from '~/shared/constants/countryCode';
import { isLocale } from '~/shared/utils/helpers';
import { useMysteryBoxSelectionsContext } from '~/shared/utils/mysteryBoxSelections';
import useGshop from '~/shared/utils/useGshop';
import {
  Button,
  ProductPrice,
  useProductDetailContext,
} from '~/techstyle-shared/react-components';
import { FormattedMessage, useIntl } from '~/techstyle-shared/react-intl';
import { useDomain } from '~/techstyle-shared/redux-core';

import errorMessages from './errorMessages';
import {
  useProductDetailCTAContext,
  ProductDetailCTAErrorTypes,
} from './ProductDetailCTAContext';

const gshopProductPricePromoStyle = css`
  ${({ theme }) => theme.pdpStyles.promoMessage.gshopPriceText}
`;

const Wrapper = styled.div`
  ${({ theme }) => theme.pdpStyles.guestCtaWrapper};
  display: flex;
  flex-direction: column;
`;

const AddToBagButton = styled(Button).attrs({
  variant: 'primary',
  size: 'lg',
})`
  ${({ theme }) => theme.pdpStyles.guestAddToBag.button}

  ${({ isFRDomain, theme }) =>
    isFRDomain &&
    css`
      background: ${theme.colors.palette.white};
      border: 1px solid ${theme.colors.alertRed};
      span {
        color: ${theme.colors.alertRed};
      }
      :hover,
      :focus,
      :focus-visible,
      :active {
        outline: none;
        background: ${theme.colors.palette.white};
      }
    `}
`;

const labelStyle = css`
  font-size: 16px;
  letter-spacing: ${-0.4 / 18}em;
  color: ${({ theme }) => theme.colors.palette.white};

  ${({ theme }) => theme.pdpStyles.guestAddToBag.label}
`;

const priceStyle = css`
  ${({ theme }) => theme.pdpStyles.guestAddToBag.productPrice.priceStyle}
`;

function GuestAddToBag({ ...rest }) {
  const intl = useIntl();
  const {
    addToCart,
    addMysteryBoxToCart,
    handleSharedValidation,
    isForceDisableCtaEnabled,
    isLoading,
    isOutOfStock,
    setErrorMessage,
    signupAsGuestCart,
    hasError,
    isAssortmentTestProductDisabled,
    onSuccessfulAddToCart,
  } = useProductDetailCTAContext();
  const productDetailFromCtx = useProductDetailContext();

  const disabled =
    isLoading ||
    isOutOfStock ||
    isForceDisableCtaEnabled ||
    isAssortmentTestProductDisabled;
  const { tld } = useDomain();
  const isFRDomain = isLocale([CountryCodes.FR], tld);

  const isMysteryBox = productDetailFromCtx.product?.isMysteryBundle;
  const { isSizeComplete, hasSoldOutSizeSelected } =
    useMysteryBoxSelectionsContext();
  const mysteryBoxCTADisabled =
    isLoading || !isSizeComplete || hasSoldOutSizeSelected;

  useEffect(() => {
    if (!hasError) {
      setErrorMessage(null);
    }
  }, [hasError, setErrorMessage]);
  const { isGshopActive, isGshopMSRPDiscountActive } = useGshop();
  const handleCTAClick = useCallback(async () => {
    try {
      signupAsGuestCart();
      await handleSharedValidation();
      if (isMysteryBox) {
        await addMysteryBoxToCart({ enableVIPCart: false });
      } else {
        await addToCart({ enableVIPCart: false });
      }

      onSuccessfulAddToCart && onSuccessfulAddToCart();
    } catch (error) {
      if (error.message === ProductDetailCTAErrorTypes.SelectorError) {
        setErrorMessage(intl.formatMessage(errorMessages.sizeSelectorError));
      }
    }
  }, [
    addToCart,
    handleSharedValidation,
    intl,
    setErrorMessage,
    signupAsGuestCart,
    onSuccessfulAddToCart,
    isMysteryBox,
    addMysteryBoxToCart,
  ]);

  return (
    <Wrapper {...rest}>
      <span tabIndex={0}>
        {isGshopMSRPDiscountActive ? (
          <FormattedMessage
            id="pdp_membership_select.guest_titlev6"
            defaultMessage="Now: {1}"
            values={{
              1: (
                <ProductPrice
                  promoPriceStyle={gshopProductPricePromoStyle}
                  showPromoGuestPrice
                  showVipPrice={false}
                  priceStyle={priceStyle}
                  showPromoPrice={false}
                />
              ),
            }}
          />
        ) : (
          <FormattedMessage
            id="pdp_membership_select.guest_titleV3"
            defaultMessage="Guest add to cart: {1}"
            values={{
              1: (
                <ProductPrice
                  showVipPrice={isGshopActive}
                  showRetailPrice={!isGshopActive}
                  showPromoPrice={false}
                  priceStyle={priceStyle}
                />
              ),
            }}
          />
        )}
      </span>

      <AddToBagButton
        type="button"
        variant="secondary"
        labelStyle={labelStyle}
        onClick={handleCTAClick}
        data-autotag={
          disabled ? 'pdp-guest-add-to-bag-disabled' : 'pdp-guest-add-to-bag'
        }
        disabled={isMysteryBox ? mysteryBoxCTADisabled : disabled}
        isFRDomain={isFRDomain}
        data-cnstrc-btn="add-to-cart"
      >
        {getProductCtaLabel(isAssortmentTestProductDisabled, isOutOfStock)}
      </AddToBagButton>
    </Wrapper>
  );
}

export default GuestAddToBag;
